import { Injectable, inject } from '@angular/core';
import { Asset, FeatureValue, FilterSort } from 'addiction-components';
import {
  BehaviorSubject,
  Observable,
  catchError,
  combineLatest,
  distinctUntilChanged,
  map,
  of,
  shareReplay,
  startWith,
  switchMap,
} from 'rxjs';
import { DEFAULT_DOCUMENT_SORT } from './document-sort-options';
import { DocumentFilterData, DocumentFilters, DocumentService } from './document.service';

export enum DocumentLoadStatus {
  LOADING = 'loading',
  READY = 'ready',
  ERROR = 'error',
}

/**
 * Rappresenta l'esito di un caricamento asincrono.
 */
export type DocumentLoadState<T> =
  | { status: DocumentLoadStatus.LOADING }
  | { status: DocumentLoadStatus.READY; data: T }
  | { status: DocumentLoadStatus.ERROR };

export enum DocumentViewMode {
  GRID = 'grid',
  LIST = 'list',
}

interface DocumentQueryState {
  categoryUuid?: string;
  filters: DocumentFilters;
  search?: string;
  sort: FilterSort;
  pages: number[];
}

interface DocumentGridData {
  assets: Asset[][];
  pages: number[];
  totalItems: number;
}

/**
 * Store RxJS locale alla rotta documenti.
 *
 * Condivide tra container, dashboard e griglia lo stato della ricerca e coordina
 * i caricamenti effettuati da {@link DocumentService}. L'istanza viene fornita
 * dalla rotta padre e non rappresenta quindi uno stato globale dell'applicazione.
 */
@Injectable()
export class DocumentStateService {
  private readonly documents = inject(DocumentService);

  /** Chiavi usate per conservare le preferenze della griglia tra sessioni diverse. */
  private readonly viewModeStorageKey = 'documentViewMode';
  private readonly gridColumnsStorageKey = 'documentGridColumns';

  /** Ogni emissione forza un nuovo caricamento delle categorie. */
  private readonly refreshCategoriesSubject = new BehaviorSubject<void>(undefined);

  /**
   * Mantiene i criteri di ricerca in un unico stato, così ogni modifica viene
   * emessa in modo atomico e non avvia richieste basate su valori intermedi.
   */
  private readonly querySubject = new BehaviorSubject<DocumentQueryState>({
    filters: {},
    sort: DEFAULT_DOCUMENT_SORT,
    pages: [0],
  });

  private readonly viewModeSubject = new BehaviorSubject<DocumentViewMode>(this.readViewMode());
  private readonly columnsSubject = new BehaviorSubject<number>(this.readColumns());

  /**
   * Pagine di asset già scaricate per la base query corrente, indicizzate per numero di pagina.
   * Evita di riscaricare le pagine precedenti quando la griglia virtuale ne richiede di nuove.
   */
  private cachedPages: Asset[][] = [];
  private cachedTotalItems = 0;

  private readonly query$ = this.querySubject.asObservable();

  /**
   * Query ridotta usata per caricare le opzioni dei filtri.
   * Ordinamento e pagine sono esclusi perché non modificano le opzioni disponibili.
   */
  private readonly filterQuery$ = this.query$.pipe(
    map(({ categoryUuid, filters, search }) => ({ categoryUuid, filters, search })),
    distinctUntilChanged(
      (previous, current) =>
        previous.categoryUuid === current.categoryUuid && previous.filters === current.filters && previous.search === current.search
    )
  );

  /**
   * Parte della query che, cambiando, invalida gli asset già scaricati.
   * Le pagine sono escluse: durante lo scroll cambiano di frequente senza modificare i criteri.
   */
  private readonly baseQuery$ = this.query$.pipe(
    map(({ categoryUuid, filters, search, sort }) => ({ categoryUuid, filters, search, sort })),
    distinctUntilChanged(
      (previous, current) =>
        previous.categoryUuid === current.categoryUuid &&
        previous.filters === current.filters &&
        previous.search === current.search &&
        previous.sort === current.sort
    )
  );

  /** Pagine attualmente richieste dalla griglia virtuale. */
  private readonly pages$ = this.query$.pipe(
    map(({ pages }) => pages),
    distinctUntilChanged(
      (previous, current) => previous.length === current.length && previous.every((page, index) => page === current[index])
    )
  );

  readonly categoryUuid$ = this.query$.pipe(
    map(({ categoryUuid }) => categoryUuid),
    distinctUntilChanged()
  );
  readonly filters$ = this.query$.pipe(
    map(({ filters }) => filters),
    distinctUntilChanged()
  );
  readonly search$ = this.query$.pipe(
    map(({ search }) => search),
    distinctUntilChanged()
  );
  readonly sort$ = this.query$.pipe(
    map(({ sort }) => sort),
    distinctUntilChanged()
  );
  readonly viewMode$ = this.viewModeSubject.asObservable();
  readonly columns$ = this.columnsSubject.asObservable();

  /**
   * Carica le categorie visibili e rappresenta l'intero ciclo della richiesta
   * con un solo valore di tipo {@link DocumentLoadState}.
   *
   * A ogni caricamento emette prima `LOADING`, senza dati, mentre attende la
   * risposta del backend. Successivamente emette uno solo dei due stati finali:
   * `READY`, con le categorie ordinate, oppure `ERROR`, senza dati.
   *
   * L'errore viene convertito in uno stato tramite `catchError`, così lo stream
   * rimane utilizzabile e il template può gestire l'errore come un normale stato UI.
   */
  readonly categoriesState$: Observable<DocumentLoadState<FeatureValue[]>> = this.refreshCategoriesSubject.pipe(
    switchMap(() =>
      this.documents.getCategories().pipe(
        map((categories) => this.readyState(this.sortCategories(categories))),
        startWith(this.loadingState<FeatureValue[]>()),
        catchError(() => of(this.errorState<FeatureValue[]>()))
      )
    ),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Categoria corrispondente al parametro di rotta corrente.
   * La ricerca viene eseguita soltanto quando le categorie sono disponibili.
   */
  readonly selectedCategory$ = combineLatest({
    categoriesState: this.categoriesState$,
    categoryUuid: this.categoryUuid$,
  }).pipe(
    map(({ categoriesState, categoryUuid }) => {
      if (categoriesState.status !== DocumentLoadStatus.READY || !categoryUuid) {
        return undefined;
      }

      return this.findCategory(categoriesState.data, categoryUuid);
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Dati necessari a costruire il pannello dei filtri per lo scope corrente.
   * Attende prima le categorie visibili, perché i relativi UUID delimitano gli
   * asset sui quali il backend deve calcolare formati, tipi e tag disponibili.
   */
  readonly filterDataState$: Observable<DocumentLoadState<DocumentFilterData>> = combineLatest({
    categoriesState: this.categoriesState$,
    query: this.filterQuery$,
  }).pipe(
    switchMap(({ categoriesState, query }) => {
      if (categoriesState.status === DocumentLoadStatus.LOADING) {
        return of(this.loadingState<DocumentFilterData>());
      }
      if (categoriesState.status === DocumentLoadStatus.ERROR) {
        return of(this.errorState<DocumentFilterData>());
      }

      const categoryUuids = this.resolveCategoryScope(categoriesState.data, query.categoryUuid);
      if (!categoryUuids.length) {
        return of(this.readyState(this.emptyFilterData()));
      }

      return this.documents.getFilters(categoryUuids, query.filters, query.search).pipe(
        map((data) => this.readyState(data)),
        startWith(this.loadingState<DocumentFilterData>()),
        catchError(() => of(this.errorState<DocumentFilterData>()))
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /**
   * Asset della griglia ricalcolati quando cambia uno dei criteri della query.
   * Anche questo caricamento dipende dalle categorie visibili, così una richiesta
   * senza categoria selezionata resta comunque limitata allo scope dell'utente.
   */
  readonly gridState$: Observable<DocumentLoadState<DocumentGridData>> = combineLatest({
    categoriesState: this.categoriesState$,
    baseQuery: this.baseQuery$,
  }).pipe(
    switchMap(({ categoriesState, baseQuery }) => {
      if (categoriesState.status === DocumentLoadStatus.LOADING) {
        return of(this.loadingState<DocumentGridData>());
      }
      if (categoriesState.status === DocumentLoadStatus.ERROR) {
        return of(this.errorState<DocumentGridData>());
      }

      // Ogni nuova base query (categoria, filtri, ordinamento o ricerca) invalida le pagine già scaricate.
      this.resetGridCache();

      const categoryUuids = this.resolveCategoryScope(categoriesState.data, baseQuery.categoryUuid);
      if (!categoryUuids.length) {
        return this.pages$.pipe(map((pages) => this.readyState(this.emptyGridData(pages))));
      }

      return this.pages$.pipe(
        switchMap((pages) => {
          // Si scaricano soltanto le pagine non ancora presenti in cache.
          const missingPages = pages.filter((page) => !this.cachedPages[page]);
          if (!missingPages.length) {
            return of(this.readyState(this.buildGridData()));
          }

          const request$ = this.documents
            .getDocuments(categoryUuids, baseQuery.filters, missingPages, baseQuery.sort, baseQuery.search)
            .pipe(
              map((data) => {
                missingPages.forEach((page) => (this.cachedPages[page] = data.assets[page] ?? []));
                this.cachedTotalItems = data.totalItems;
                return this.readyState(this.buildGridData());
              })
            );

          // Lo skeleton compare solo al primo caricamento; durante lo scroll gli asset già visibili restano.
          return this.cachedPages.length === 0
            ? request$.pipe(
                startWith(this.loadingState<DocumentGridData>()),
                catchError(() => of(this.errorState<DocumentGridData>()))
              )
            : request$.pipe(catchError(() => of(this.readyState(this.buildGridData()))));
        })
      );
    }),
    shareReplay({ bufferSize: 1, refCount: true })
  );

  /** Seleziona la categoria della rotta e riavvia il caricamento dalla prima pagina. */
  setCategoryUuid(uuid?: string): void {
    const query = this.querySubject.value;
    if (query.categoryUuid !== uuid) {
      this.querySubject.next({ ...query, categoryUuid: uuid, pages: [0] });
    }
  }

  /**
   * Applica insieme i filtri e la categoria selezionata, evitando richieste
   * intermedie basate sulla vecchia categoria.
   */
  applyFilters(filters: DocumentFilters, categoryUuid?: string): void {
    this.querySubject.next({ ...this.querySubject.value, categoryUuid, filters, pages: [0] });
  }

  /**
   * La ricerca testuale è globale alle categorie e riparte senza filtri o pagine pregresse.
   */
  setSearch(search: string | null | undefined): void {
    const normalizedSearch = search?.trim() || undefined;
    this.querySubject.next({
      ...this.querySubject.value,
      categoryUuid: undefined,
      filters: {},
      search: normalizedSearch,
      pages: [0],
    });
  }

  /** Cambia l'ordinamento e riavvia il caricamento dalla prima pagina. */
  setSort(sort: FilterSort): void {
    this.querySubject.next({ ...this.querySubject.value, sort, pages: [0] });
  }

  /** Aggiorna le pagine richieste dalla griglia virtuale dopo averle normalizzate. */
  setPages(pages: number[]): void {
    if (pages.length) {
      // La griglia virtuale può richiedere pagine duplicate o fuori ordine.
      const normalizedPages = [...new Set(pages)].sort((a, b) => a - b);
      this.querySubject.next({ ...this.querySubject.value, pages: normalizedPages });
    }
  }

  /** Aggiorna e persiste la modalità di visualizzazione scelta dall'utente. */
  setViewMode(viewMode: DocumentViewMode): void {
    localStorage.setItem(this.viewModeStorageKey, viewMode);
    this.viewModeSubject.next(viewMode);
  }

  /** Limita il numero di colonne all'intervallo supportato e persiste il risultato. */
  setColumns(columns: number): void {
    const normalizedColumns = Math.min(8, Math.max(2, columns));
    localStorage.setItem(this.gridColumnsStorageKey, String(normalizedColumns));
    this.columnsSubject.next(normalizedColumns);
  }

  /** Ripristina i criteri del dashboard mantenendo ordinamento e preferenze visive. */
  resetDashboardState(): void {
    this.querySubject.next({
      ...this.querySubject.value,
      categoryUuid: undefined,
      filters: {},
      search: undefined,
      pages: [0],
    });
  }

  /** Richiede esplicitamente un nuovo caricamento delle categorie. */
  refreshCategories(): void {
    this.refreshCategoriesSubject.next();
  }

  /** Costruisce uno stato di caricamento privo di dati. */
  private loadingState<T>(): DocumentLoadState<T> {
    return { status: DocumentLoadStatus.LOADING };
  }

  /** Associa i dati ricevuti a uno stato completato correttamente. */
  private readyState<T>(data: T): DocumentLoadState<T> {
    return { status: DocumentLoadStatus.READY, data };
  }

  /** Costruisce uno stato di errore privo di dati obsoleti o fittizi. */
  private errorState<T>(): DocumentLoadState<T> {
    return { status: DocumentLoadStatus.ERROR };
  }

  /** Rappresenta una risposta valida senza asset per le pagine richieste. */
  private emptyGridData(pages: number[]): DocumentGridData {
    const assets: Asset[][] = [];
    return { assets, pages, totalItems: 0 };
  }

  /** Svuota le pagine in cache quando cambia la base query. */
  private resetGridCache(): void {
    this.cachedPages = [];
    this.cachedTotalItems = 0;
  }

  /**
   * Costruisce i dati per la griglia a partire dalle pagine in cache.
   * Ogni pagina resta al proprio indice (pagina 3 → assets[3]) e le pagine
   * non ancora caricate lasciano l'indice non valorizzato, così la griglia
   * virtuale conosce la posizione degli asset senza bisogno di averli tutti.
   */
  private buildGridData(): DocumentGridData {
    const assets: Asset[][] = [];
    const pages: number[] = [];
    this.cachedPages.forEach((pageAssets, page) => {
      // forEach su un array sparso salta le posizioni non ancora caricate.
      assets[page] = pageAssets;
      pages.push(page);
    });
    return { assets, pages, totalItems: this.cachedTotalItems };
  }

  /** Rappresenta una risposta valida nella quale non sono disponibili filtri. */
  private emptyFilterData(): DocumentFilterData {
    return { format: {}, assetType: {}, tags: [] };
  }

  /** Ordina ricorsivamente ogni livello dell'albero delle categorie senza modificare la risposta originale. */
  private sortCategories(categories: FeatureValue[]): FeatureValue[] {
    return [...categories]
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
      .map((category) => ({ ...category, featureValues: this.sortCategories(category.featureValues ?? []) }));
  }

  /** Cerca una categoria per UUID attraversando ricorsivamente l'intero albero. */
  private findCategory(categories: FeatureValue[], uuid: string): FeatureValue | undefined {
    for (const category of categories) {
      if (category.uuid === uuid) {
        return category;
      }
      const child = this.findCategory(category.featureValues ?? [], uuid);
      if (child) {
        return child;
      }
    }
    return undefined;
  }

  /**
   * Restituisce gli UUID utilizzabili nelle richieste alla media library.
   * Senza selezione include tutte le categorie visibili; con una selezione accetta
   * l'UUID soltanto se appartiene a quell'elenco.
   */
  private resolveCategoryScope(categories: FeatureValue[], selectedUuid?: string): string[] {
    const allowedUuids = this.flattenCategoryUuids(categories);
    // Impedisce di interrogare una categoria non presente tra quelle restituite per l'utente.
    if (!selectedUuid) {
      return allowedUuids;
    }
    return allowedUuids.includes(selectedUuid) ? [selectedUuid] : [];
  }

  /**
   * Trasforma l'albero delle categorie nell'elenco dei relativi UUID.
   * @param categories
   * @returns
   */
  private flattenCategoryUuids(categories: FeatureValue[]): string[] {
    return categories.flatMap((category) => [category.uuid, ...this.flattenCategoryUuids(category.featureValues ?? [])]);
  }

  /** Recupera la modalità persistita, usando la griglia per valori assenti o non validi. */
  private readViewMode(): DocumentViewMode {
    const storedViewMode = localStorage.getItem(this.viewModeStorageKey);

    return storedViewMode === DocumentViewMode.LIST ? DocumentViewMode.LIST : DocumentViewMode.GRID;
  }

  /**
   * Recupera il numero di colonne persistito oppure sceglie un valore iniziale
   * in base alla larghezza corrente della finestra.
   */
  private readColumns(): number {
    const columns = Number(localStorage.getItem(this.gridColumnsStorageKey));
    if (Number.isFinite(columns) && columns >= 2 && columns <= 8) {
      return columns;
    }
    if (window.innerWidth <= 600) {
      return 2;
    }
    if (window.innerWidth <= 900) {
      return 3;
    }
    if (window.innerWidth <= 1300) {
      return 4;
    }
    return 6;
  }
}
